import { TypedDocumentNode, gql } from '@apollo/client';
import { useRef } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useQueryString from '@sorare/core/src/hooks/useQueryString';

import {
  NBAMyClubPageQuery,
  NBAMyClubPageQueryVariables,
} from './__generated__/layout.graphql';
import { UserCards } from './cards/page';
import { LayoutData } from './layoutData';

const NBA_MY_CLUB_PAGE_QUERY = gql`
  query NBAMyClubPageQuery($slug: String!, $name: String, $hasDeck: Boolean!) {
    user(slug: $slug) {
      slug
      id
      nbaUserProfile {
        id
        deck(name: $name) @include(if: $hasDeck) {
          slug
          ...NbaUserCards_deck
        }
        decks {
          nodes {
            slug
          }
        }
      }
      ...NbaUserCards_user
    }
  }
  ${
    // eslint-disable-next-line sorare/no-detached-fragments
    UserCards.fragments.user
  }
  ${
    // eslint-disable-next-line sorare/no-detached-fragments
    UserCards.fragments.deck
  }
` as TypedDocumentNode<NBAMyClubPageQuery, NBAMyClubPageQueryVariables>;

const MyClub = () => {
  const { slug } = useParams();
  const currentDeckName = useQueryString(SEARCH_PARAMS.DECK) ?? '';
  const initialDeckName = useRef(currentDeckName);
  const { data } = useQuery(NBA_MY_CLUB_PAGE_QUERY, {
    variables: {
      slug: slug!,
      name: initialDeckName.current,
      hasDeck: !!initialDeckName.current,
    },
  });
  const { user } = data || {};

  if (!user) {
    return <LoadingIndicator fullHeight />;
  }

  const userId = idFromObject(user.id);
  return (
    <Outlet
      context={
        {
          userId,
          user,
          initialDeckName: initialDeckName.current,
          currentDeckName,
        } satisfies LayoutData
      }
    />
  );
};

export default MyClub;
